/* eslint jsx-a11y/label-has-associated-control: ["error", { "assert": "htmlFor" }] */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, Prompt } from 'react-router-dom';
import DateField from '../common/DateField';
import SchoolCalendarService from '../../services/SchoolCalendarService';
import './schoolCalendar.scss';

const { validateCalendar } = require('../../../LambdaFunctions/shared/schoolCalendar');

function validationMessage(calendar) {
  if (calendar.teachingPeriod.tillDate < calendar.teachingPeriod.fromDate) return 'Koniec zajęć nie może poprzedzać początku.';
  try {
    validateCalendar(calendar);
    return '';
  } catch (error) {
    return 'Sprawdź daty zajęć i przerw. Podaj nazwy przerw, a daty wybierz w obrębie roku szkolnego. Koniec zakresu nie może poprzedzać początku.';
  }
}

function CalendarForm({ schoolYearStart, contextKey, onUnsavedChange }) {
  const [snapshot, setSnapshot] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const active = useRef(true);
  const nextBreak = useRef(0);
  const settingsPath = `/manage/settings?schoolYearStart=${schoolYearStart}`;
  const minDate = `${schoolYearStart}-09-01`;
  const maxDate = `${schoolYearStart + 1}-08-31`;

  const acceptSnapshot = (data) => {
    setSnapshot(data);
    setCalendar({ schoolYearStart,
      timeZone: data.timeZone || 'Europe/Warsaw',
      teachingPeriod: data.teachingPeriod || { fromDate: '', tillDate: '' },
      breaks: data.breaks || [] });
    setDirty(false);
    setConflict(false);
  };

  useEffect(() => () => { active.current = false; }, []);
  useEffect(() => {
    onUnsavedChange(dirty || saving);
    return () => onUnsavedChange(false);
  }, [dirty, saving, onUnsavedChange]);
  useEffect(() => {
    let current = true;
    const controller = new AbortController();
    setLoading(true);
    setError('');
    setSuccess(false);
    if (!contextKey) {
      setError('Nie można ustalić szkoły. Zaloguj się ponownie.');
      setLoading(false);
      return () => {};
    }
    SchoolCalendarService.getCalendar(schoolYearStart, { signal: controller.signal }).then((data) => {
      if (current) acceptSnapshot(data);
    }).catch(() => {
      if (current) setError('Nie udało się pobrać kalendarza szkoły. Spróbuj ponownie.');
    }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; controller.abort(); };
  }, [schoolYearStart, contextKey, loadAttempt]);

  useEffect(() => {
    const warn = (event) => {
      if (dirty || saving) {
        event.preventDefault();
        // Browsers require returnValue in addition to preventDefault for this event.
        Object.assign(event, { returnValue: '' });
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [dirty, saving]);

  const change = (updated) => { setCalendar(updated); setDirty(true); setSuccess(false); setError(''); };
  const changePeriod = (field, value) => change({ ...calendar, teachingPeriod: { ...calendar.teachingPeriod, [field]: value } });
  const changeBreak = (breakId, field, value) => change({ ...calendar,
    breaks: calendar.breaks.map(item => (item.breakId === breakId ? { ...item, [field]: value } : item)) });
  const addBreak = () => {
    nextBreak.current += 1;
    change({ ...calendar, breaks: [...calendar.breaks, { breakId: `break-${Date.now()}-${nextBreak.current}`, name: '', fromDate: '', tillDate: '' }] });
  };
  const save = async (event) => {
    event.preventDefault();
    if (saving || conflict) return;
    const invalid = validationMessage(calendar);
    if (invalid) { setError(invalid); return; }
    setSaving(true);
    setError('');
    setSuccess(false);
    try {
      const data = await SchoolCalendarService.saveCalendar(schoolYearStart, { expectedRevision: snapshot.revision, calendar });
      if (active.current) { acceptSnapshot(data); setSuccess(true); }
    } catch (failure) {
      if (active.current) {
        if (failure.statusCode === 409 && failure.code !== 'SCHEDULE_UPDATING') {
          setConflict(true);
          setError('Kalendarz zmienił się od otwarcia formularza. Twoje zmiany nie zostały zapisane. Wczytaj aktualną wersję przed ponowną edycją.');
        } else if (failure.code === 'SCHEDULE_UPDATING') {
          setError('Trwa aktualizacja planu szkoły. Odczekaj chwilę i spróbuj zapisać ponownie.');
        } else if (failure.statusCode === 403) {
          setError('Nie masz uprawnień do zmiany kalendarza szkoły.');
        } else {
          setError('Nie udało się zapisać kalendarza. Sprawdź dane i połączenie, a następnie spróbuj ponownie.');
        }
      }
    } finally { if (active.current) setSaving(false); }
  };

  return (
    <section className="school-calendar-panel school-calendar-editor">
      <Prompt when={dirty || saving} message="Masz niezapisane zmiany kalendarza. Czy opuścić formularz?" />
      <Link className="btn manage-button manage-teal-button mb-3" to={settingsPath}>← Powrót do ustawień</Link>
      <h2>Kalendarz szkoły</h2>
      <p>Rok szkolny {schoolYearStart}/{schoolYearStart + 1}</p>
      <p className="alert alert-info">
        Zapisane daty są dostępne w podglądzie kalendarza. Automatyczne uwzględnianie ich w lekcjach, obecnościach, pakietach i powiadomieniach nie jest jeszcze włączone.
      </p>
      {error && <p role="alert" className="alert alert-danger">{error}</p>}
      {success && <p role="status" className="alert alert-success">Kalendarz został zapisany.</p>}
      {loading && <p role="status">Ładowanie kalendarza szkoły…</p>}
      {!loading && !snapshot && <button type="button" className="btn manage-button manage-teal-button" onClick={() => setLoadAttempt(loadAttempt + 1)}>Spróbuj ponownie</button>}
      {conflict && (
        <button type="button" className="btn manage-button manage-teal-button mb-3" onClick={() => setLoadAttempt(loadAttempt + 1)}>
          Wczytaj aktualny kalendarz i odrzuć moje zmiany
        </button>
      )}
      {!loading && snapshot && snapshot.status === 'updating' && <p role="status">Trwa aktualizacja kalendarza. Wróć do ustawień i spróbuj ponownie za chwilę.</p>}
      {!loading && snapshot && snapshot.status === 'configured' && !snapshot.integrationPending && <p>Ten kalendarz wymaga aktualizacji przez proces publikacji zmian w planie szkoły.</p>}
      {!loading && calendar && snapshot.status !== 'updating' && (snapshot.status === 'notConfigured' || snapshot.integrationPending) && (
        <form onSubmit={save}>
          <fieldset disabled={saving}>
            <legend>Okres prowadzenia zajęć</legend>
            <div className="row">
              <div className="form-group col-md-6">
                <label htmlFor="calendar-start">Początek zajęć</label>
                <DateField
                  id="calendar-start"
                  aria-label="Początek zajęć"
                  className="form-control"
                  type="date"
                  required
                  min={minDate}
                  max={maxDate}
                  value={calendar.teachingPeriod.fromDate}
                  onChange={event => changePeriod('fromDate', event.target.value)}
                />
              </div>
              <div className="form-group col-md-6">
                <label htmlFor="calendar-end">Koniec zajęć</label>
                <DateField
                  id="calendar-end"
                  aria-label="Koniec zajęć"
                  className="form-control"
                  type="date"
                  required
                  min={minDate}
                  max={maxDate}
                  value={calendar.teachingPeriod.tillDate}
                  onChange={event => changePeriod('tillDate', event.target.value)}
                />
              </div>
            </div>
            <h3>Przerwy i dni wolne</h3>
            <p>Daty obejmują pierwszy i ostatni dzień przerwy. Dla jednego dnia podaj tę samą datę w obu polach.</p>
            {calendar.breaks.length === 0 && <p>Nie dodano przerw ani dni wolnych.</p>}
            {calendar.breaks.map((item, index) => (
              <div className="school-calendar-editor-break" key={item.breakId}>
                <div className="form-group">
                  <label htmlFor={`break-name-${item.breakId}`}>Nazwa przerwy {index + 1}</label>
                  <input id={`break-name-${item.breakId}`} className="form-control" required value={item.name} onChange={event => changeBreak(item.breakId, 'name', event.target.value)} />
                </div>
                <div className="form-group">
                  <label htmlFor={`break-start-${item.breakId}`}>Początek przerwy {index + 1}</label>
                  <DateField
                    id={`break-start-${item.breakId}`}
                    className="form-control"
                    type="date"
                    required
                    min={minDate}
                    max={maxDate}
                    value={item.fromDate}
                    onChange={event => changeBreak(item.breakId, 'fromDate', event.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor={`break-end-${item.breakId}`}>Koniec przerwy {index + 1}</label>
                  <DateField
                    id={`break-end-${item.breakId}`}
                    className="form-control"
                    type="date"
                    required
                    min={minDate}
                    max={maxDate}
                    value={item.tillDate}
                    onChange={event => changeBreak(item.breakId, 'tillDate', event.target.value)}
                  />
                </div>
                <button
                  type="button"
                  className="btn manage-button manage-teal-button"
                  aria-label={`Usuń przerwę ${index + 1}`}
                  onClick={() => change({ ...calendar, breaks: calendar.breaks.filter(entry => entry.breakId !== item.breakId) })}
                >Usuń
                </button>
              </div>
            ))}
            <button type="button" className="btn manage-button manage-teal-button" onClick={addBreak}>Dodaj przerwę</button>
            <div className="school-calendar-editor-actions">
              <Link className="btn manage-button manage-teal-button" to={settingsPath}>Powrót</Link>
              <button className="btn manage-button manage-red-button" type="submit" disabled={conflict}>{saving ? 'Zapisywanie…' : 'Zapisz kalendarz'}</button>
            </div>
          </fieldset>
        </form>
      )}
    </section>
  );
}

const editorProps = { schoolYearStart: PropTypes.number.isRequired, contextKey: PropTypes.string.isRequired, onUnsavedChange: PropTypes.func };
const editorDefaults = { onUnsavedChange: () => {} };
CalendarForm.propTypes = editorProps;
CalendarForm.defaultProps = editorDefaults;

export default function SchoolCalendarEditor({ schoolYearStart, contextKey, onUnsavedChange }) {
  return <CalendarForm key={JSON.stringify([schoolYearStart, contextKey])} schoolYearStart={schoolYearStart} contextKey={contextKey} onUnsavedChange={onUnsavedChange} />;
}
SchoolCalendarEditor.propTypes = editorProps;
SchoolCalendarEditor.defaultProps = editorDefaults;
